import React from 'react';
import { motion } from 'framer-motion';
import { useParams, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faExternalLinkAlt, faRocket } from '@fortawesome/free-solid-svg-icons';

const InfoPage = () => {
  const { toolId } = useParams();
  const navigate = useNavigate();

  const toolInfo = {
    'live-rating-calculator': {
      title: 'Live Rating Calculator',
      icon: '📈',
      description: 'Calculate your rating changes in real-time based on game results. Track your progress and predict future rating movements with our advanced ELO calculation system.',
      features: [
        'Real-time ELO calculations',
        'Support for different time controls',
        'Rating change predictions',
        'Performance tracking',
        'Export rating history'
      ],
      status: 'Coming Soon',
      launchUrl: null
    },
    'swiss-manager': {
      title: 'Swiss Manager',
      icon: '🏟️',
      description: 'Manage Swiss system tournaments with automatic pairing generation, round management, and results tracking. Perfect for chess clubs and tournament organizers.',
      features: [
        'Automatic Swiss pairings',
        'Round management',
        'Player registration',
        'Results tracking',
        'Tournament reports'
      ],
      status: 'Coming Soon',
      launchUrl: null
    },
    'ask-a-gm': {
      title: 'Ask a GM',
      icon: '🤔',
      description: 'Get personalized chess advice from Grandmasters. Submit your positions, ask strategic questions, and learn from the best players in the world.',
      features: [
        'Direct GM consultation',
        'Position analysis',
        'Strategic advice',
        'Opening recommendations',
        'Endgame guidance'
      ],
      status: 'Coming Soon',
      launchUrl: null
    }
  };

  const tool = toolInfo[toolId] || {
    title: 'Tool Not Found',
    icon: '❓',
    description: 'This tool is not available yet.',
    features: [],
    status: 'Not Available',
    launchUrl: null
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3 }}
      className="max-w-4xl mx-auto px-4"
    >
      {/* Back Button */}
      <motion.button
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.3, delay: 0.1 }}
        onClick={() => navigate('/')}
        className="mb-8 flex items-center gap-2 text-gray-400 hover:text-gray-300 transition-colors duration-200"
      >
        <FontAwesomeIcon icon={faArrowLeft} />
        Back to Directory
      </motion.button>

      {/* Tool Header */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
        className="text-center mb-12"
      >
        <div className="text-6xl mb-4">{tool.icon}</div>
        <h1 className="text-4xl md:text-5xl font-bold text-gray-100 mb-4">
          {tool.title}
        </h1>
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600/20 border border-blue-500/30 rounded-full text-blue-400 text-sm font-medium">
          <FontAwesomeIcon icon={faRocket} className="text-xs" />
          {tool.status}
        </div>
      </motion.div>

      {/* Description */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.3 }}
        className="bg-gray-800 border border-gray-700 rounded-xl p-8 mb-8"
      >
        <h2 className="text-2xl font-semibold text-gray-100 mb-4">About This Tool</h2>
        <p className="text-gray-300 text-lg leading-relaxed">
          {tool.description}
        </p>
      </motion.div>

      {/* Features */}
      {tool.features.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="bg-gray-800 border border-gray-700 rounded-xl p-8 mb-8"
        >
          <h2 className="text-2xl font-semibold text-gray-100 mb-6">Key Features</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {tool.features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3, delay: 0.5 + index * 0.1 }}
                className="flex items-center gap-3"
              >
                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                <span className="text-gray-300">{feature}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Action Button */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.6 }}
        className="text-center"
      >
        {tool.launchUrl ? (
          <a
            href={tool.launchUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 px-8 py-4 bg-blue-600 hover:bg-blue-500 text-white font-semibold rounded-xl transition-all duration-200 text-lg"
          >
            Launch Tool
            <FontAwesomeIcon icon={faExternalLinkAlt} />
          </a>
        ) : (
          <div className="inline-flex items-center gap-3 px-8 py-4 bg-gray-700 text-gray-400 font-semibold rounded-xl text-lg cursor-not-allowed">
            <FontAwesomeIcon icon={faRocket} />
            Coming Soon
          </div>
        )}
      </motion.div>

      {/* Notify Me Section */}
      {!tool.launchUrl && (
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.7 }}
          className="mt-12 bg-gray-800 border border-gray-700 rounded-xl p-8 text-center"
        >
          <h3 className="text-xl font-semibold text-gray-100 mb-4">Get Notified When It's Ready</h3>
          <p className="text-gray-400 mb-6">Be the first to know when this tool launches.</p>
          <div className="flex gap-3 max-w-md mx-auto">
            <input
              type="email"
              placeholder="Enter your email"
              className="flex-1 bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-gray-100 placeholder-gray-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
            />
            <button className="px-6 py-3 bg-blue-600 hover:bg-blue-500 text-white font-medium rounded-lg transition-colors duration-200">
              Notify Me
            </button>
          </div>
        </motion.div>
      )}
    </motion.div>
  );
};

export default InfoPage;